import { Button, Form, Modal, Result, Upload, message } from "antd";
import React, { useEffect, useState } from "react";
import { InboxOutlined } from "@ant-design/icons";
import { useUploadFiles } from "utils/media";
import { FileTypes } from "enum/file-type";
import { useApplyJobs } from "api/jobs/useApplyJobs";
import { useSaveJobs } from "api/jobs/useSaveJobs";
import { useGetEnumeratorProfileInfo } from "api/enumerator/Profile/useGetProfile";
import { storage } from "utils/storage";

type JobApplyFormProps = {
  id: string | undefined;
  isModalOpen: boolean;
  setIsModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
  isSaveJobs: boolean;
  enumeratorId: string;
  onSaveSuccess: (jobId: string) => void; // Add a callback for save success
};

const JobApplyForm: React.FC<JobApplyFormProps> = ({
  isModalOpen,
  setIsModalOpen,
  id,
  isSaveJobs,
  enumeratorId,
}) => {
  const [form] = Form.useForm();
  const [resume, setResume] = useState<string | null>(null);

  const applyJobMutation = useApplyJobs();
  const saveJobsMutation = useSaveJobs();

  const {
    onChange,
    uploadImage,
    documentID,
    fileList,
    setFileList,
    setDocumentId,
  } = useUploadFiles();

  const user_id = storage.getUserFromStorage().uid;

  // Fetch enumerator profile
  const { data: enumeratorProfile, isLoading } = useGetEnumeratorProfileInfo(user_id);

  // Set resume from enumerator profile
  useEffect(() => {
    if (enumeratorProfile?.cv) {
      if (typeof enumeratorProfile.cv.file === "string") {
        setResume(enumeratorProfile.cv.file);
      } else if (
        Array.isArray(enumeratorProfile.cv.file) &&
        enumeratorProfile.cv.file.length > 0
      ) {
        setResume(enumeratorProfile.cv.file[0].url);
      } else {
        setResume(null);
      }
    }
  }, [enumeratorProfile]);

  const handleSubmit = async () => {
    if (!id) {
      message.error("Job ID not found.");
      return;
    }

    if (!resume) {
      message.error("Resume not found in profile. Please upload it first.");
      return;
    }

    const payload = {
      id,
      data: { coverLetter: documentID || undefined, resume },
      enumeratorId,
    };

    const mutation = isSaveJobs ? saveJobsMutation : applyJobMutation;

    mutation.mutate(payload, {
      onSuccess: () => {
        message.success(
          isSaveJobs
            ? "Job saved successfully!"
            : "Application submitted successfully!"
        );
        window.location.reload(); // Refresh the page
      },
      onError: () => {
        message.error("Something went wrong. Please try again.");
      },
    });
  };

  const getModalContent = () => {
    if (isSaveJobs) {
      return (
        <Result
          status="info"
          title="Are you sure you want to save this job?"
          subTitle="Click 'Save' to add this job to your saved list."
        />
      );
    }

    return (
      <Form form={form} layout="vertical" onFinish={handleSubmit}>
        <Form.Item label="Cover Letter">
          <Upload.Dragger
            name="files"
            maxCount={1}
            multiple={false}
            onChange={onChange}
            fileList={fileList}
            customRequest={(options) =>
              uploadImage(options, {
                type: FileTypes.COVER_LETTER,
                id: id,
                enumeratorId,
                service: FileTypes.COVER_LETTER,
                tags: ["customer", "documents"],
                description: "Customer document upload",
                isConsentDocument: true,
                shouldNotAllowMultipleFiles: true,
              })
            }
            beforeUpload={(file) => {
              const isPDF = file.type === "application/pdf";
              if (!isPDF) {
                message.error(`${file.name} is not a PDF file.`);
              }
              return isPDF || Upload.LIST_IGNORE;
            }}
            onRemove={(file) => {
              const updatedFileList = fileList?.filter(
                (f) => f.url !== file.url
              );
              setFileList(updatedFileList);
              setDocumentId(null);
            }}
          >
            <p className="ant-upload-drag-icon">
              <InboxOutlined />
            </p>
            <p className="ant-upload-text">
              Click or drag file to this area to upload
            </p>
            <p className="ant-upload-hint">
              Support for a single PDF upload only.
            </p>
          </Upload.Dragger>
        </Form.Item>
        <p>
          <strong>Resume:</strong>{" "}
          {isLoading
            ? "Loading resume..."
            : resume
              ? "Resume found in profile"
              : "No resume uploaded"}
        </p>
      </Form>
    );
  };

  return (
    <Modal
      destroyOnClose
      onCancel={() => {
        setIsModalOpen(false);
        setFileList([]);
        setDocumentId(null);
      }}
      title={isSaveJobs ? "" : "Upload your Cover Letter"}
      open={isModalOpen}
      footer={
        [
          <Button
            key="cancel"
            onClick={() => setIsModalOpen(false)}
          >
            Cancel
          </Button>,
          <Button
            key="saveOrSubmit"
            type="primary"
            onClick={handleSubmit}
            loading={
              isSaveJobs
                ? saveJobsMutation.isPending
                : applyJobMutation.isPending
            }
          >
            {isSaveJobs ? "Save" : "Submit"}
          </Button>,
        ]
      }
    >
      {getModalContent()}
    </Modal>
  );
};

export default JobApplyForm;